import { useMemo, useState } from "react";
import { AlertTriangle, BookCheck, CheckCircle2, FileWarning, FileX2, Users } from "lucide-react";

import { usePageBack } from "../../context/PageBackContext.js";
import { cn } from "../../lib/utils.js";
import { DropdownSelect } from "../../components/DropdownButton.js";
import { SearchInput } from "../../components/ui/search-input.js";
import { Progress } from "../../components/ui/progress.js";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table.js";
import type { PaginationMeta } from "../../components/ui/pagination.js";
import {
  ReportPage, FilterBar, ErrorBanner, OrgFilters, ExportButton, CardGrid, ReportStatCard, ReportTableFrame, ReportFooter, PageSizeSelect, YesNoBadge,
  STATUS_GROUP_OPTIONS, TableScrollStart, TableToolbar, dropdownProps, stickyName, stickyCode, NAME_WIDTH, CODE_WIDTH, type OpenState, type YesNoState,
} from "../../components/reports/ReportKit.js";
import {
  useUrlFilters, useDebouncedSearch, useReport, useOrgMeta, toParams, downloadReport,
} from "../../components/reports/useReport.js";
import { SignedCopyDownload } from "../../components/PolicySignPanel.js";

interface DocCell { s: YesNoState; t?: string }
interface DocColumn { key: string; label: string }
/** A policy sign-off, with the signed copy when signed. */
interface PolicyCell extends DocCell { url?: string | null }
interface DocRow {
  id: string; name: string; code: string; department: string | null; office: string | null; status: string;
  docs: Record<string, DocCell>; ctc: DocCell; handbook: DocCell; policies: Record<string, PolicyCell>; done: number; of: number;
}
interface DocumentReport {
  columns: DocColumn[];
  /** Policy documents every employee signs: one column each, added as HR publishes them. */
  policyColumns: DocColumn[];
  rows: DocRow[];
  /** Loaded by a separate request (`part=cards`), so a page turn never refetches it. */
  cards?: {
    employees: number; complete: number; incomplete: number;
    mostMissing: { label: string; count: number } | null; handbookPending: number; expired: number;
  };
  footer: Record<string, number>;
  meta: PaginationMeta;
}

const DEFAULTS = {
  search: "", officeIds: "", departmentIds: "", statusGroup: "current",
  docType: "", docState: "", completion: "", page: "1", limit: "25",
};

const STATE_OPTIONS = [
  { value: "missing", label: "Missing" },
  { value: "has", label: "Uploaded" },
  { value: "expired", label: "Expired" },
];
const COMPLETION_OPTIONS = [
  { value: "complete", label: "All documents uploaded" },
  { value: "incomplete", label: "Missing any document" },
];

// The cards describe the whole scope; the document filters narrow only the table.
const ROW_ONLY = ["docType", "docState", "completion"];

const API = "/reports/hr/documents";

export default function DocumentReportPage() {
  usePageBack("/dashboard");
  const [f, setF] = useUrlFilters(DEFAULTS);
  const [search, setSearch] = useDebouncedSearch(f.search, (v) => setF({ search: v }));
  const [open, setOpen] = useState<string | null>(null);
  const openState: OpenState = { open, setOpen };
  const org = useOrgMeta();

  const params = useMemo(() => toParams(f), [f]);
  const { data, cards: c, loading, error } = useReport<DocumentReport>(API, params, "DocumentReportPage", ROW_ONLY);

  // Column choices for the "document + state" filter: every document column plus
  // the two employment/policy columns.
  const docOptions = useMemo(() => [
    ...(data?.columns ?? []).map((c) => ({ value: c.key, label: c.label })),
    { value: "ctc", label: "CTC Breakdown" },
    { value: "handbook", label: "Handbook Acknowledged" },
  ], [data?.columns]);

  const columns = data?.columns ?? [];
  const policyColumns = data?.policyColumns ?? [];
  // A handbook every employee signs has its own sign-off column; the onboarding
  // acknowledgement column would only repeat it.
  const showHandbook = !policyColumns.some((p) => p.key === "HANDBOOK");
  const rows = data?.rows ?? [];

  return (
    <ReportPage
      title="Document Reports"
      description="Which of the employee profile's documents each employee has on file, and who has signed the policies every employee signs."
    >
      <CardGrid>
        <ReportStatCard icon={Users} tone="brand" label="Employees" value={c?.employees ?? "—"} />
        <ReportStatCard icon={CheckCircle2} tone="success" label="All Documents" value={c?.complete ?? "—"} caption="Every column uploaded" />
        <ReportStatCard icon={FileX2} tone="danger" label="Missing Documents" value={c?.incomplete ?? "—"} caption="At least one missing" />
        <ReportStatCard
          icon={AlertTriangle} tone="warning" label="Most Missing"
          value={c?.mostMissing ? c.mostMissing.count : "—"}
          caption={c?.mostMissing?.label ?? "Nothing missing"}
        />
        <ReportStatCard icon={BookCheck} tone="accent" label="Handbook Pending" value={c?.handbookPending ?? "—"} caption="Not acknowledged" />
        <ReportStatCard icon={FileWarning} tone="rose" label="Expired" value={c?.expired ?? "—"} caption="Documents past expiry" />
      </CardGrid>

      <FilterBar>
        <SearchInput className="w-60" placeholder="Search name, code or email" value={search} onChange={(e) => setSearch(e.target.value)} />
        <OrgFilters meta={org} officeIds={f.officeIds} departmentIds={f.departmentIds} onChange={setF} openState={openState} />
        <DropdownSelect
          value={f.statusGroup} onChange={(v) => setF({ statusGroup: v })} options={STATUS_GROUP_OPTIONS}
          placeholder="Current employees" ariaLabel="Employees" clearable={false} {...dropdownProps("status", openState)}
        />
        <DropdownSelect
          value={f.docType} onChange={(v) => setF({ docType: v, docState: v ? f.docState || "missing" : "" })} options={docOptions}
          placeholder="Any document" ariaLabel="Document" panelClassName="max-h-80" {...dropdownProps("docType", openState)}
        />
        {f.docType && (
          <DropdownSelect
            value={f.docState} onChange={(v) => setF({ docState: v })} options={STATE_OPTIONS}
            placeholder="Missing" ariaLabel="Document state" clearable={false} {...dropdownProps("docState", openState)}
          />
        )}
        <DropdownSelect
          value={f.completion} onChange={(v) => setF({ completion: v })} options={COMPLETION_OPTIONS}
          placeholder="Any completion" ariaLabel="Completion" {...dropdownProps("completion", openState)}
        />
      </FilterBar>

      <TableToolbar>
        <PageSizeSelect limit={f.limit} onLimit={(v) => setF({ limit: v })} openState={openState} />
        <ExportButton onExport={() => downloadReport(API, params, "document-report.xlsx")} />
      </TableToolbar>

      <ErrorBanner message={error} />

      <ReportTableFrame loading={loading} empty={rows.length === 0} emptyText="No employees match these filters." contentKey={`${columns.length}|${policyColumns.length}`}>
        <Table className="w-max min-w-full">
          <TableHeader>
            <TableRow className="bg-surface-50">
              <TableHead className={cn(stickyName, "bg-surface-50")}><div className={NAME_WIDTH}><TableScrollStart />Employee Name</div></TableHead>
              <TableHead className={cn(stickyCode, "bg-surface-50")}><div className={CODE_WIDTH}>Emp Code</div></TableHead>
              {columns.map((col) => (
                <TableHead key={col.key} className="px-2 text-center normal-case">
                  <div className="w-24 whitespace-normal leading-tight" title={col.label}>
                    {col.label}
                  </div>
                </TableHead>
              ))}
              <TableHead className="border-l border-surface-200 px-2 text-center normal-case"><div className="w-24 leading-tight whitespace-normal">CTC Breakdown</div></TableHead>
              {showHandbook && <TableHead className="px-2 text-center normal-case"><div className="w-28 leading-tight whitespace-normal">Handbook Acknowledged</div></TableHead>}
              {policyColumns.map((col, i) => (
                <TableHead key={`policy-${col.key}`} className={cn("px-2 text-center normal-case", i === 0 && "border-l border-surface-200")}>
                  <div className="w-28 whitespace-normal leading-tight" title={`${col.label}: signed by every employee, each year`}>{col.label} Signed</div>
                </TableHead>
              ))}
              <TableHead className="sticky right-0 z-10 bg-surface-50 px-3 text-center">Completion</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.id} className="group hover:bg-surface-50">
                <TableCell className={cn(stickyName, "bg-surface-0 group-hover:bg-surface-50")}>
                  <div className={cn(NAME_WIDTH, "truncate font-semibold text-surface-900")} title={[r.name, r.department, r.office].filter(Boolean).join(" · ")}>{r.name}</div>
                </TableCell>
                <TableCell className={cn(stickyCode, "bg-surface-0 text-surface-600 group-hover:bg-surface-50")}>
                  <div className={cn(CODE_WIDTH, "truncate")}>{r.code}</div>
                </TableCell>
                {columns.map((col) => {
                  const cell = r.docs[col.key] ?? { s: "N" as const };
                  return <TableCell key={col.key} className="text-center"><YesNoBadge state={cell.s} title={cell.t} /></TableCell>;
                })}
                <TableCell className="border-l border-surface-200 text-center"><YesNoBadge state={r.ctc.s} title={r.ctc.t} /></TableCell>
                {showHandbook && (
                  <TableCell className="text-center">
                    <YesNoBadge state={r.handbook.s} title={r.handbook.t}>{r.handbook.s === "Y" ? "Signed" : "Pending"}</YesNoBadge>
                  </TableCell>
                )}
                {policyColumns.map((col, i) => {
                  const cell = r.policies[col.key] ?? { s: "N" as const };
                  return (
                    <TableCell key={`policy-${col.key}`} className={cn("text-center", i === 0 && "border-l border-surface-200")}>
                      <div className="flex items-center justify-center gap-1">
                        <YesNoBadge state={cell.s} title={cell.t}>{cell.s === "Y" ? "Signed" : "Pending"}</YesNoBadge>
                        {cell.url && <SignedCopyDownload url={cell.url} title={col.label} />}
                      </div>
                    </TableCell>
                  );
                })}
                <TableCell className="sticky right-0 z-10 bg-surface-0 px-3 group-hover:bg-surface-50">
                  <div className="flex w-24 flex-col gap-1">
                    <span className="text-[12px] font-semibold tabular-nums text-surface-900">{r.done} / {r.of}</span>
                    <Progress
                      value={r.of ? (r.done / r.of) * 100 : 0}
                      barClassName={r.done === r.of ? "bg-[var(--color-success)]" : r.done === 0 ? "bg-[var(--color-error)]" : "bg-[var(--color-warning-accent)]"}
                    />
                  </div>
                </TableCell>
              </TableRow>
            ))}
            <TableRow className="bg-surface-50 font-semibold">
              <TableCell className={cn(stickyName, "bg-surface-50 text-[12px] text-surface-700")}>Uploaded</TableCell>
              <TableCell className={cn(stickyCode, "bg-surface-50 text-[11px] text-surface-500")}>of {data?.meta.total ?? 0}</TableCell>
              {columns.map((col) => (
                <TableCell key={col.key} className="text-center text-[12px] tabular-nums">{data?.footer[col.key] ?? 0}</TableCell>
              ))}
              <TableCell className="border-l border-surface-200 text-center text-[12px] tabular-nums">{data?.footer["ctc"] ?? 0}</TableCell>
              {showHandbook && <TableCell className="text-center text-[12px] tabular-nums">{data?.footer["handbook"] ?? 0}</TableCell>}
              {policyColumns.map((col, i) => (
                <TableCell key={`policy-${col.key}`} className={cn("text-center text-[12px] tabular-nums", i === 0 && "border-l border-surface-200")}>
                  {data?.footer[`policy:${col.key}`] ?? 0}
                </TableCell>
              ))}
              <TableCell className="sticky right-0 z-10 bg-surface-50" />
            </TableRow>
          </TableBody>
        </Table>
      </ReportTableFrame>

      <ReportFooter
        meta={data?.meta} onPage={(p) => setF({ page: String(p) })}
      />
    </ReportPage>
  );
}
